import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { createStaff, updateStaff } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  first_name: "", last_name: "", staff_id: "", phone: "", email: "",
  birthday: "", department: "", department_head: "", room: "", school_campus: "",
  website: "", facebook: "", instagram: "", x_handle: "", avatar_url: "",
  schedule: Array.from({ length: 8 }, (_, i) => ({
    period: `${i + 1}${["st", "nd", "rd"][i] || "th"} Pd`,
    time: "",
    subject: "",
  })),
};

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

export const StaffFormDialog = ({ open, onOpenChange, existing, onSaved, year }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({ ...EMPTY, ...existing, schedule: existing.schedule?.length ? existing.schedule : EMPTY.schedule });
    } else {
      setForm(EMPTY);
    }
  }, [existing, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPeriod = (i, k, v) =>
    setForm((f) => {
      const schedule = f.schedule.map((p, idx) => (idx === i ? { ...p, [k]: v } : p));
      return { ...f, schedule };
    });

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (existing) {
        await updateStaff(existing.id, payload);
        toast.success("Staff member updated");
      } else {
        await createStaff(payload, year);
        toast.success("Staff member added");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="staff-form-dialog"
        className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A1F44]">
            {existing ? "Edit Staff Member" : "Add Staff Member"}
          </DialogTitle>
          <DialogDescription className="text-[#0A1F44]/60">
            Fill in the details and daily bell schedule below.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider">First Name</Label>
            <Input data-testid="form-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Last Name</Label>
            <Input data-testid="form-last-name" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Staff ID</Label>
            <Input data-testid="form-staff-id" className={inputCls} value={form.staff_id} onChange={(e) => set("staff_id", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Birthday (MM/DD)</Label>
            <Input data-testid="form-birthday" placeholder="04/17" className={inputCls} value={form.birthday} onChange={(e) => set("birthday", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Phone</Label>
            <Input data-testid="form-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Email</Label>
            <Input data-testid="form-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Department</Label>
            <Input data-testid="form-department" className={inputCls} value={form.department} onChange={(e) => set("department", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Admin Supervisor</Label>
            <Input data-testid="form-dept-head" className={inputCls} value={form.department_head} onChange={(e) => set("department_head", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">School Campus</Label>
            <Input data-testid="form-campus" className={inputCls} value={form.school_campus} onChange={(e) => set("school_campus", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Room</Label>
            <Input data-testid="form-room" className={inputCls} value={form.room} onChange={(e) => set("room", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Photo URL</Label>
            <Input data-testid="form-avatar" className={inputCls} value={form.avatar_url} onChange={(e) => set("avatar_url", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Website</Label>
            <Input data-testid="form-website" className={inputCls} value={form.website} onChange={(e) => set("website", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Facebook</Label>
            <Input data-testid="form-facebook" className={inputCls} value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Instagram</Label>
            <Input data-testid="form-instagram" className={inputCls} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">X Handle</Label>
            <Input data-testid="form-x" className={inputCls} value={form.x_handle} onChange={(e) => set("x_handle", e.target.value)} />
          </div>
        </div>

        <div className="pt-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-[#0A1F44]/60">Bell Schedule</Label>
          <div className="mt-3 space-y-2">
            {form.schedule.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className={`${inputCls} col-span-3`} placeholder="Period" value={p.period} onChange={(e) => setPeriod(i, "period", e.target.value)} />
                <Input className={`${inputCls} col-span-3`} placeholder="Time" value={p.time} onChange={(e) => setPeriod(i, "time", e.target.value)} />
                <Input className={`${inputCls} col-span-6`} placeholder="Class / Subject" value={p.subject} onChange={(e) => setPeriod(i, "subject", e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            data-testid="form-cancel"
            variant="outline"
            className="rounded-none border-[#0A1F44] text-[#0A1F44] hover:bg-[#E6EAF1]"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="form-save"
            disabled={saving}
            className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]"
            onClick={submit}
          >
            {saving ? "Saving…" : existing ? "Save Changes" : "Add Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
